import { Spacer, VDBText } from "@components/atoms";
import LabelView from "@components/molecules/LabelView";
import { useTypographyStore } from "@redux/slices/typographySlice";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Platform, TouchableOpacity, View } from "react-native";
import { useTheme } from "styled-components/native";
import styled from "styled-components/native";
import { useSelector } from "react-redux";
import ReportAnIssueModal from "@components/molecules/ReportAnIssue";
import { ProductCategory } from "@utils/constants";
import { CustomisationKey, ProductHeaderDetailProps } from "../types";
import { SpaceType } from "@components/atoms/Spacer/types";
import { PriceModes } from "@components/molecules/PriceModeCard/types";
import { useGetPdpSectionLayout } from "../../../hooks/useGetSectionLayout";
import { CustomtOption, PdpSections } from "@utils/customization";
import { getAvailabilityStatus } from "./utils";
import { useFilteredSection } from "../../../hooks/useFilteredOptions";
import { css } from "styled-components/native";
import { SearchFilterTypeEnum } from "@components/atoms/Filters/utils";
import { Diamond } from "@src/types/DiamondDetail";
import {
  getFormattedStockNumber,
  isLinkedPair,
  isPair,
} from "@utils/product/productValidators";
import LabelContainer from "./LabelContainer";
import { DefaultTheme } from "styled-components/native";
import { useUserInfo } from "@redux/slices/userInfoSlice";

const ProductHeaderDetail = ({
  itemDetail,
  priceMode = 1,
  productColor,
  productAlphaColor,
  productName,
  hideReportIssue = false,
  productType,
  showAvailabilityDot,
  isModal = false,
}: ProductHeaderDetailProps) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const typography = useTypographyStore();
  const isCustomApp = useSelector((state: any) => state.appState?.isCustomApp);
  const [showReportAnIssueModal, setShowReportAnIssueModal] = useState(false);
  const dateFormat = "MMMM D, YYYY [at] hh:mm A";
  const [customOptions, setCustomOptions] = useState<CustomtOption[]>([]);
  const { color, text, colorKey } = getAvailabilityStatus(
    itemDetail?.available
  );
  const { isVdbMarketPlace } = useUserInfo();
  const stoneType = isPair(itemDetail)
    ? SearchFilterTypeEnum.PAIRS
    : SearchFilterTypeEnum.SINGLE_STONES;

  const sectionLayout = useGetPdpSectionLayout(
    PdpSections.SUMMARY,
    productType,
    stoneType
  );

  const filteredSections = useFilteredSection(theme, priceMode, sectionLayout);

  useEffect(() => {
    setCustomOptions(filteredSections);
  }, [filteredSections]);

  const handleOpenReportAnIssue = () => {
    setShowReportAnIssueModal(true);
  };

  const getFilterDetailData = (searchText: string): any => {
    const filteredArray = customOptions.filter(
      (item) => item.name === searchText
    );

    return filteredArray;
  };

  const objReportIssue = getFilterDetailData(CustomisationKey.report_issue);
  const objStockNumber = getFilterDetailData(CustomisationKey.stock_no);
  const objStatus = getFilterDetailData(CustomisationKey.status);
  const objLastRefresh = getFilterDetailData(CustomisationKey.last_refresh);
  const objShippingInformation = getFilterDetailData(
    CustomisationKey.shipping_information
  )[0];
  return (
    <Container>
      <HeaderTextContainer>
        {!isModal &&
        priceMode === PriceModes.WHITE &&
        !hideReportIssue &&
        objReportIssue &&
        objReportIssue?.length !== 0 ? (
          <TouchableOpacity onPress={handleOpenReportAnIssue}>
            <View pointerEvents="none">
              <ReportContainer>
                <LabelView
                  leftIcon={objReportIssue[0]?.icon?.data}
                  leftIconTheme={theme[productColor]}
                  itemRightText={t(objReportIssue[0]?.label)}
                  rightPrefixTextTheme={theme[productColor]}
                  valueTypographyStyle={typography["Body-M"]}
                  leftIconSpace={SpaceType.ExtraSmall}
                  ellipsize={false}
                />
              </ReportContainer>
              <Spacer size={SpaceType.ExtraSmall} />
            </View>
          </TouchableOpacity>
        ) : null}
        {productName && productName?.length > 1 && (
          <LabelContainer
            backgroundColor={productAlphaColor}
            productColor={productColor}
            productName={productName}
          />
        )}
      </HeaderTextContainer>
      <LabelTextContainer>
        <VDBText
          color={theme["core-05"]}
          type={typography["Large-B"]}
          selectable={Platform.OS === "web"}
        >
          {itemDetail?.short_title}
        </VDBText>
      </LabelTextContainer>
      {stoneType === SearchFilterTypeEnum.PAIRS && isLinkedPair(itemDetail) ? (
        itemDetail?.children?.map((childDetail: Diamond) => (
          <StockTextContainer>
            {objStockNumber && objStockNumber?.length !== 0 && (
              <LabelView
                itemLeftText={t(objStockNumber[0]?.label, "Stock#")}
                keyTypographyStyle={typography["Body-R"]}
                valueTypographyStyle={typography["Body-M"]}
                itemLeftTextTheme={theme["core-06"]}
                rightPrefixTextTheme={theme["core-05"]}
                ellipsize={false}
                itemRightText={
                  getFormattedStockNumber(
                    childDetail?.stock_num,
                    isCustomApp,
                    isVdbMarketPlace(),
                    childDetail?.bmh_request_type
                  ) || "-"
                }
                leftPostfixText=""
              />
            )}
          </StockTextContainer>
        ))
      ) : (
        <StockTextContainer>
          {objStockNumber && objStockNumber?.length !== 0 && (
            <LabelView
              itemLeftText={t(objStockNumber[0]?.label, "Stock#")}
              keyTypographyStyle={typography["Body-R"]}
              valueTypographyStyle={typography["Body-M"]}
              itemLeftTextTheme={theme["core-06"]}
              rightPrefixTextTheme={theme["core-05"]}
              ellipsize={false}
              itemRightText={
                getFormattedStockNumber(
                  itemDetail?.stock_num,
                  isCustomApp,
                  isVdbMarketPlace(),
                  itemDetail?.bmh_request_type
                ) || "-"
              }
              leftPostfixText=""
            />
          )}
        </StockTextContainer>
      )}
      {objShippingInformation &&
        itemDetail?.[objShippingInformation?.response_key] && (
          <ShippingInformationContainer
            backgroundColor={objShippingInformation?.backgroundColor}
          >
            <VDBText
              color={
                theme[
                  objShippingInformation?.textColor as keyof DefaultTheme
                ] as string
              }
              type={typography["Body-M"]}
              selectable={Platform.OS === "web"}
            >
              {itemDetail?.[objShippingInformation?.response_key]}
            </VDBText>
          </ShippingInformationContainer>
        )}
      {objStatus && objStatus?.length !== 0 && showAvailabilityDot && (
        <LabelTextContainer>
          <LabelView
            rightIconTheme={color}
            itemLeftText={t(objStatus[0].label)}
            keyTypographyStyle={typography["Body-R"]}
            valueTypographyStyle={typography["Body-M"]}
            itemLeftTextTheme={theme["core-06"]}
            rightPrefixTextTheme={theme["core-05"]}
            itemRightText={text}
            availabilityIcon={colorKey}
            leftPostfixText=""
            rightIconSize={10}
            rightIconAlign={"center"}
          />
        </LabelTextContainer>
      )}
      {productType !== ProductCategory.JEWELRY &&
        productType !== ProductCategory.GEMSTONE &&
        priceMode === 1 &&
        objLastRefresh &&
        objLastRefresh?.length !== 0 && (
          <LabelTextContainer>
            <LabelView
              itemLeftText={t(objLastRefresh[0].label)}
              keyTypographyStyle={typography["Body-R"]}
              valueTypographyStyle={typography["Body-M"]}
              itemLeftTextTheme={theme["core-06"]}
              rightPrefixTextTheme={theme["core-05"]}
              itemRightText={moment(itemDetail?.last_updated_at).format(
                dateFormat
              )}
              hasExtraSpace={true}
              leftPostfixText=""
            />
          </LabelTextContainer>
        )}

      {showReportAnIssueModal && (
        <ReportAnIssueModal
          isOpen={showReportAnIssueModal}
          onClose={() => setShowReportAnIssueModal(false)}
          itemDetail={itemDetail}
        />
      )}
    </Container>
  );
};

const Container = styled.View`
  padding-top: ${SpaceType.Double}px;
  width: 100%;
  gap: ${SpaceType.Basic}px;
`;

const LabelTextContainer = styled.View`
  margin-left: 4px;
  margin-right: 8px;
`;

const StockTextContainer = styled.View<any>`
  margin: 2px ${SpaceType.Double}px 2px ${SpaceType.ExtraSmall}px;
  padding-right: 16px;
  max-width: ${({ width, theme }) => (theme.isMobile ? width + "px" : "340px")};
  margin-bottom: 2px;
`;

const ReportContainer = styled.View<any>`
  margin-bottom: 4px;
`;

const HeaderTextContainer = styled.View`
  flex-direction: row;
  justify-content: space-between;
  ${({ theme }) =>
    theme.isMobile &&
    css`
      width: 100%;
    `}
  margin-left: 4px;
`;

const ShippingInformationContainer = styled.View<{
  backgroundColor: string;
}>`
  background-color: ${({ theme, backgroundColor }) =>
    theme[backgroundColor as keyof DefaultTheme] as string};
  padding: ${SpaceType.Small}px ${SpaceType.Basic}px;
  align-self: flex-start;
  border-radius: ${SpaceType.Basic}px;
  max-width: 100%;
`;

export default ProductHeaderDetail;
